<template>
  <div class="offer-letter-record-list-container">
    <TableList
      v-rect-loading="loading"
      class="offer-letter-record-list"
      row-key="id"
      showSearchFilter
      :showCreateBtn="false"
      :title="$t(`${i18nPrefix}.title.offerLetterInviteRecordList`)"
      :listData="inviteRecordList"
      :tableColumns="tableColumns"
      :disabledActionBtn="disabledActionBtn"
      :actionBtnTooltip="actionBtnTooltip"
      :actionBtn="actionBtn"
      :pagination="pagination"
      @pagination-change="handlePaginationChange"
    >
      <template #candidateName="{ row }">
        <div v-auto-tooltip class="candidate-name" @click="handleOpenCandidateDetail(row)">{{ row.candidate.name }}</div>
      </template>
      <template #search-filter>
        <el-input v-model="search.keyword" class="keyword-search" clearable prefix-icon="el-icon-search" :placeholder="$t(`${i18nPrefix}.placeholder.search`)" @input="handleSearch"></el-input>
        <el-select v-model="search.status" class="status-select" clearable @change="handleSearch">
          <el-option
            v-for="(value, key) in OFFER_LETTER_INVITE_STATUS"
            :key="key"
            :label="$t(`${i18nPrefix}.INVITE_STATUS.${key}`)"
            :value="value"
          ></el-option>
        </el-select>
      </template>
      <template #search-filter-btn>
        <el-button v-rect-loading="exportStatus.loading" class="blue-btn export-btn" @click="handleExport">{{ $t(`${i18nPrefix}.btn.export`) }}</el-button>
      </template>
    </TableList>
    <DialogWrapper
      close-on-cancel
      class="offer-letter-template-dialog"
      titleI18nKey="admin.application.candidate.title.offerLetterSelectTemplate"
      :show.sync="offerLetterTemplateDialog.show"
      :loading="offerLetterTemplateDialog.loading"
      @save="handleSelectTemplate"
    >
      <el-select v-model="selectedTemplateId">
        <el-option
          v-for="(template, index) in templateList"
          :key="index"
          :label="template.name"
          :value="template.id"
        ></el-option>
      </el-select>
    </DialogWrapper>
    <InviteOfferLetterDialog
      :show.sync="inviteOfferLetterDialog.show"
      :selectedTemplateId="inviteOfferLetterDialog.templateId"
      :templateList="templateList"
      :templateFieldList="templateFieldList"
      :candidateName="inviteOfferLetterDialog.candidateName"
      :companyId="inviteOfferLetterDialog.companyId"
      :company-name="isEn() ? inviteOfferLetterDialog.company?.name : inviteOfferLetterDialog.company?.cn_name"
      :jobId="inviteOfferLetterDialog.jobId"
      :job-name="isEn() ? inviteOfferLetterDialog.job?.title : inviteOfferLetterDialog.job?.cn_title"
      @save="handleSaveSendOffer"
      @cancel="handleCancelSendOffer"
    ></InviteOfferLetterDialog>
  </div>
</template>

<script>
import _ from 'lodash'
import moment from 'moment'
import { mapActions } from 'vuex'
import TableList from '@/components/tableList/allowDragSortTable.vue'
import DialogWrapper from '@/components/dialog/dialogWrapper.vue'
import InviteOfferLetterDialog from '@/views/adminNew/candidate/profile/dialog/inviteOfferLetterDialog.vue'
import { isEn, addI18nProperty, getConstantI18nValue } from '@/i18n'
import { OFFER_LETTER_INVITE_STATUS } from '@/constant/company'
import { AsyncTask } from '@/utils/common'

export default {
  components: {
    TableList,
    DialogWrapper,
    InviteOfferLetterDialog
  },
  data () {
    return {
      isEn,
      i18nPrefix: 'admin.company.offerLetterInviteRecordManagement',
      OFFER_LETTER_INVITE_STATUS,
      loading: false,
      inviteRecordList: [],
      pagination: {
        page: 1,
        pageSize: 20,
        pageSizes: [20, 50, 100],
        total: 0
      },
      search: {
        keyword: null,
        status: null
      },
      actionBtn: {
        sendNotice: {
          labelI18nKey: 'admin.company.offerLetterInviteRecordManagement.btn.sendNotice',
          show: (row) => { return row.status === OFFER_LETTER_INVITE_STATUS.INVITED },
          onclick: this.handleSendInviteOfferNotice
        },
        resendInviteOffer: {
          labelI18nKey: 'admin.company.offerLetterInviteRecordManagement.btn.resendInvite',
          show: (row) => { return row.status === OFFER_LETTER_INVITE_STATUS.INVITED },
          onclick: this.handleResendInviteOfferLetter
        }
      },
      searchDebounce: null,
      exportStatus: {
        loading: false
      },
      templateList: [],
      selectedTemplateId: null,
      templateFieldList: [],
      offerLetterTemplateDialog: {
        show: false,
        loading: false
      },
      inviteOfferLetterDialog: {
        show: false,
        templateId: null,
        candidateName: null,
        candidateId: null,
        jobId: null
      }
    }
  },
  computed: {
    hasOnlyActionOnMyCandidatePermission () {
      return this.hasAnyPermission('PERM_ONLY_ACTION_ON_MY_CANDIDATE')
    },
    tableColumns () {
      const columns = [
        {
          label: this.$t(`${this.i18nPrefix}.label.inviteOfferLetterDate`),
          dataKey: 'created_time',
          formatter: (val) => {
            return moment(val).format('YYYY/MM/DD')
          }
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.inviteCompany`),
          dataKey: 'companyI18nName'
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.inviteJobName`),
          dataKey: 'jobI18nName'
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.inviteOfferLetterTemplate`),
          dataKey: 'offer_letter_template.name'
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.inviteRecruiter`),
          dataKey: 'creator.full_name'
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.inviteCandidate`),
          dataKey: 'candidate.name',
          slotName: 'candidateName'
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.inviteOfferLetterStatus`),
          dataKey: 'status',
          formatter: (val) => {
            return getConstantI18nValue(OFFER_LETTER_INVITE_STATUS, val, `${this.i18nPrefix}.INVITE_STATUS`)
          }
        }
      ]
      return columns
    }
  },
  created () {
    this.initPage()
  },
  methods: {
    ...mapActions('offerLetter', [
      'getOfferLetterInviteRecordList',
      'getOfferLetterTemplateList',
      'getOfferLetterTemplateFieldList',
      'createExportOfferInviteRecordTask',
      'getExportOfferInviteRecordTaskInfo'
    ]),
    ...mapActions('application', [
      'resendInviteCandidateOfferLetter',
      'sendOfferLetterNotice'
    ]),
    initPage () {
      this.loadInviteRecordList()
    },
    async loadInviteRecordList () {
      this.loading = true
      try {
        const params = {
          status: this.search.status,
          keyword: this.search.keyword,
          page_num: this.pagination.page,
          page_size: this.pagination.pageSize
        }
        const resp = await this.getOfferLetterInviteRecordList(params)
        if (resp) {
          _.forEach(resp.result, item => {
            addI18nProperty(item, 'companyI18nName', 'job.company.cn_name', 'job.company.name')
            addI18nProperty(item, 'jobI18nName', 'job.cn_title', 'job.title')
          })
          this.inviteRecordList = resp.result
          this.pagination.total = resp.total_count
        }
      } finally {
        this.loading = false
      }
    },
    disabledActionBtn (rowData) {
      return this.hasOnlyActionOnMyCandidatePermission && !rowData.job.is_my_job
    },
    actionBtnTooltip (rowData) {
      return this.disabledActionBtn(rowData) ? this.$t('admin.application.applicantList.message.disabledActionCandidateInJob') : null
    },
    async loadTemplateList () {
      const resp = await this.getOfferLetterTemplateList({ disabled_pagination: 1, is_enabled: 1 })
      if (resp) {
        this.templateList = resp.result
      }
    },
    async loadTemplateFiledList () {
      const resp = await this.getOfferLetterTemplateFieldList()
      if (resp) {
        this.templateFieldList = _.filter(resp, { type: 'HR' })
      }
    },
    handlePaginationChange () {
      this.loadInviteRecordList()
    },
    handleSearch () {
      if (!this.searchDebounce) {
        this.searchDebounce = _.debounce(() => {
          this.loadInviteRecordList()
        }, 500)
      }
      this.searchDebounce()
    },
    async handleResendInviteOfferLetter (row) {
      this.offerLetterTemplateDialog.show = true
      this.offerLetterTemplateDialog.loading = true
      try {
        await Promise.all([this.loadTemplateList(), this.loadTemplateFiledList()])
        this.inviteOfferLetterDialog.candidateId = row.candidate.id
        this.inviteOfferLetterDialog.jobId = row.job.id
        this.inviteOfferLetterDialog.companyId = row.job.company.id
        this.inviteOfferLetterDialog.candidateName = row.candidate.name
        this.inviteOfferLetterDialog.company = row.job.company
        this.inviteOfferLetterDialog.job = row.job
      } finally {
        this.offerLetterTemplateDialog.loading = false
      }
    },
    handleSelectTemplate () {
      if (!this.selectedTemplateId) {
        return
      }
      this.offerLetterTemplateDialog.show = false
      this.inviteOfferLetterDialog.templateId = this.selectedTemplateId
      this.inviteOfferLetterDialog.show = true
    },
    async handleSaveSendOffer (offerLetterInfo, callback) {
      try {
        const params = {
          job_id: this.inviteOfferLetterDialog.jobId,
          candidate_id: this.inviteOfferLetterDialog.candidateId,
          template_id: this.inviteOfferLetterDialog.templateId,
          template_field_info: offerLetterInfo
        }
        const resp = await this.resendInviteCandidateOfferLetter(params)
        if (resp) {
          this.inviteOfferLetterDialog.show = false
          this.$message({
            type: 'success',
            message: this.$t('admin.application.candidate.message.reInviteOfferSuccess')
          })
        }
        this.handleSearch()
      } finally {
        callback()
      }
    },
    handleCancelSendOffer () {
      this.inviteOfferLetterDialog.show = false
    },
    async handleSendInviteOfferNotice (row) {
      this.loading = true
      try {
        const params = {
          job_id: row.job.id,
          candidate_id: row.candidate.id
        }
        const resp = await this.sendOfferLetterNotice(params)
        if (resp) {
          this.$message({
            type: 'success',
            message: this.$t('admin.application.candidate.message.sendOfferNoticeSuccess')
          })
          this.handleSearch()
        }
      } finally {
        this.loading = false
      }
    },
    handleExport () {
      this.exportStatus.loading = true
      const exportOfferRecordStatus = {
        finished: false,
        task: null
      }
      exportOfferRecordStatus.finished = false
      exportOfferRecordStatus.task = new AsyncTask(() => this.createExportOfferInviteRecordTask({
        status: this.search.status,
        keyword: this.search.keyword
      }), (taskId) => this.getExportOfferInviteRecordTaskInfo({
        task_id: taskId
      }), (taskId) => {
        window.location.href = this.$DjangoBaseUrl(`/django_rest/offer-letter/invite-record/export/?task_id=${taskId}&download=1`)
        exportOfferRecordStatus.finished = false
        exportOfferRecordStatus.task = null
        this.exportStatus.loading = false
      })
    },
    handleOpenCandidateDetail (row) {
      const candidateId = row.candidate.id
      const jobId = row.job.id
      window.open(`/v-admin/application/profile/offer?candidate_id=${candidateId}&job_id=${jobId}`)
    }
  }
}
</script>

<style lang="scss" scoped>
.offer-letter-record-list-container {
  .offer-letter-record-list {
    .candidate-name {
      color: #0096cc;
      &:hover {
        cursor: pointer;
      }
    }
    .keyword-search {}
    .status-select {
      margin-left: 14px;
    }
    .export-btn {
      position: absolute;
      right: 24px;
      ::v-deep {
        .el-loading-spinner {
          margin-top: -14px;

          .loading-spinner {
            .spinner {
              height: 28px;
            }
          }
        }
      }
    }
  }
}
</style>
